import { ProbeResult, REGIONS } from "../types";

interface RoutingInsightProps {
  results: ProbeResult[];
}

export default function RoutingInsight({ results }: RoutingInsightProps) {
  const withIP = results.filter((r) => r.resolved_ip && !r.error);
  if (withIP.length < 2) return null;

  const ipToRegions: Record<string, string[]> = {};
  for (const r of withIP) {
    const ip = r.resolved_ip!;
    if (!ipToRegions[ip]) ipToRegions[ip] = [];
    ipToRegions[ip].push(r.region);
  }

  const uniqueIPs = Object.keys(ipToRegions);
  const isSingle = uniqueIPs.length === 1;

  const accentColor = isSingle ? "#39D98A" : "#5B8DEF";

  return (
    <div style={{
      marginTop: 20,
      padding: "16px 20px",
      background: "rgba(18,18,18,0.5)",
      border: `1px solid ${isSingle ? "rgba(57,217,138,0.15)" : "rgba(91,141,239,0.2)"}`,
      borderRadius: 3,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: isSingle ? 4 : 12 }}>
        <span style={{
          display: "inline-block",
          width: 8, height: 8, borderRadius: "50%",
          background: accentColor,
          flexShrink: 0,
        }} />
        <span style={{ fontSize: 13, fontWeight: 700, color: accentColor }}>
          {isSingle ? "Single origin" : `GeoDNS / Anycast detected — ${uniqueIPs.length} distinct servers`}
        </span>
      </div>

      {isSingle ? (
        <p style={{ margin: 0, fontSize: 13, color: "#98A1AB" }}>
          All {withIP.length} regions resolved to{" "}
          <code style={{ fontFamily: "monospace", color: "#EFF2F5" }}>{uniqueIPs[0]}</code>
          . Traffic is served by a single origin or a shared anycast IP.
        </p>
      ) : (
        <>
          <p style={{ margin: "0 0 12px", fontSize: 13, color: "#98A1AB" }}>
            Different regions resolved to different IPs — your endpoint uses GeoDNS or anycast routing.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {uniqueIPs.map((ip) => (
              <div key={ip} style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                <code style={{
                  fontFamily: "monospace", fontSize: 12,
                  color: "#EFF2F5",
                  background: "rgba(18,18,18,0.6)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  padding: "3px 8px", borderRadius: 3,
                  whiteSpace: "nowrap", flexShrink: 0,
                  minWidth: 110, textAlign: "right",
                }}>
                  {ip}
                </code>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, paddingTop: 2 }}>
                  {ipToRegions[ip].map((region) => (
                    <span key={region} style={{
                      fontSize: 11, fontWeight: 600,
                      color: "#98A1AB",
                      background: "rgba(18,18,18,0.5)",
                      border: "1px solid rgba(255,255,255,0.1)",
                      borderRadius: 3, padding: "2px 7px",
                    }}>
                      {REGIONS[region] ?? region}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
